import { format, subHours } from 'date-fns';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../../hooks/useGetGeneralAppData';
import CompanyService from '../../../../../services/CompanyService';
import ConsultsService from '../../../../../services/ConsultsService';
import VtItensService from '../../../../../services/VtItensService';
import ExportXlsx from '../../../../../utils/ExportXlsx';
import floatToCurrency from '../../../../../utils/floatToCurrency';
import getCurrentDateWithHyphen from '../../../../../utils/getCurrentDateWithHyphen';
import onlyNumbers from '../../../../../utils/onlyNumbers';
import { CompanyFromApiType, FileType, GetCompanyDataResponseType, GetPartnersListApiResponse, IFilesListByPeriodApiResponse, IReportApiResponse, ListsForSearchingConsultsApiResponse, OptionType, PeriodType, Value, ValuePiece, VtItensListApiResponse } from '../types';

interface IuseDownloadGeneralReports {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setDownloadModalShow: Dispatch<SetStateAction<boolean>>;
  downloadModalShow: boolean;
}

interface VT {
  CPF: string;
  NOME: string;
  COMPRA_VT: string;
  VT_COD: string;
  VT_DESCR: string;
  VT_TARIFA: number;
  VT_QTD: number;
  MATRICULA: string;
  COD_EMPRESA: string;
}
/* eslint-disable @typescript-eslint/no-empty-function */
export default function useDownloadGeneralReports({ setIsLoading, setDownloadModalShow, downloadModalShow }: IuseDownloadGeneralReports) {
  const [specificPeriod, setSpecificPeriod] = useState<Value>([new Date(), new Date()]);
  const [specificPeriodLabel, setSpecificPeriodLabel] = useState(`De ${format(new Date(), 'dd/MM/yyyy')} até ${format(new Date(), 'dd/MM/yyyy')}`);

  const [selectedCompany, setSelectedCompany] = useState<{ value: string | number, label: string }>({ value: '', label: 'Selecione uma Empresa' });
  const [selectedPartner, setSelectedPartner] = useState<{ value: string | number, label: string }>({ value: '', label: 'Selecione um Parceiro' });
  const [selectedReport, setSelectedReport] = useState<{ value: number, label: string }>({ value: 0, label: 'Selecione um relatório' });

  const [generatedConsultsReportType, setGeneratedConsultsReportType] = useState<'all' | 'byCompany' | 'byPartner'>('all');
  const [selectedFileType, setSelectedFileType] = useState<FileType>('preAdm');
  const [selectedFile, setSelectedFile] = useState<OptionType>({ value: '', label: 'Selecione um arquivo' });
  const [partnersOptions, setPartnerOptions] = useState<{ value: string | number, label: string }[]>([]);
  const [mrFilesOptions, setMrFilesOptions] = useState<{ value: string, label: string, fileType: FileType }[]>([]);
  const [baseFilesOptions, setBaseFilesOptions] = useState<{ value: string, label: string, fileType: FileType }[]>([]);
  const [preAdmFilesOptions, setPreAdmFilesOptions] = useState<{ value: string, label: string, fileType: FileType }[]>([]);
  const filesBySelectedFileType = useMemo(() => (mrFilesOptions?.filter((file) => file.fileType === selectedFileType)), [mrFilesOptions, selectedFileType]);

  const [companiesList, setCompaniesList] = useState<{ value: string | number, label: string }[]>([]);

  const [periodBeingSelected, setPeriodBeingSelected] = useState({
    month: (new Date()).getMonth() + 1,
    year: (new Date()).getFullYear(),
  } as PeriodType);

  const { apiCall } = useApiCall();
  const { userCode, userProfile, permissions, usersCompanyCode, usersCompanyLabel, isCustomer, isDifferentSubdomain, canAccessFinancial  } = useGetGeneralAppData();

  const companiesOptions = companiesList;

  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const monthsMap: { [key: number]: string } = useMemo(() => ({
    1: 'Janeiro',
    2: 'Fevereiro',
    3: 'Março',
    4: 'Abril',
    5: 'Maio',
    6: 'Junho',
    7: 'Julho',
    8: 'Agosto',
    9: 'Setembro',
    10: 'Outubro',
    11: 'Novembro',
    12: 'Dezembro',
  }), []);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const routingPermissions = permissions.find((x) => x.codPag === 6);

  // [61, 50, 5, 57]

  function getFirstAndLastPeriod(value: Value): { first: ValuePiece, last: ValuePiece } {
    if (value instanceof Array) {
      // Se for um Range, acessa o primeiro e último elemento
      const [first, last] = value;
      return { first, last };
    } else {
      // Se for um único valor, tanto o primeiro quanto o último são o próprio valor
      return { first: value, last: value };
    }
  }

  function handleSelectedFileChange(wp: OptionType) {
    setSelectedFile(wp);
  }

  const handleSpecificPeriodChange = useCallback(async (value: Value) => {
    if (Array.isArray(value)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const adjustedByTimezone = value.map((date) => subHours(date!, 3));
      setSpecificPeriod([adjustedByTimezone[0], adjustedByTimezone[1]]);

      const periodLabel = `De ${format(value[0] as Date, 'dd/MM/yyyy')} até ${format(value[1] as Date, 'dd/MM/yyyy')}`;
      setSpecificPeriodLabel(periodLabel);

      const { first, last } = getFirstAndLastPeriod(value);

      if ([61, 50, 5, 57, 601, 503, 6010, 5011, 5021, 571, 572, 504, 701, 702, 703, 704].some((x) => x == selectedReport.value)) {
        return;
      }

      await apiCall(({
        apiToCall: ConsultsService.getListOfFilesByPeriod,
        queryParams: {
          codUsu: userCode,
          perfilUsu: userProfile,
          codEmpresa: selectedCompany.value,
          data1: first!.toISOString().split('T')[0],
          data2: last!.toISOString().split('T')[0],
        },
        onStartLoad: () => setIsLoading(true),
        onEndLoad: () => setIsLoading(false),
        actionAfterResponse: (response: IFilesListByPeriodApiResponse) => {
          if (!response.codigo) {
            toast.error(`Não foi possível buscar os arquivos (${response.msg})`);
            return;
          }

          const { temArqBase, temArqPA } = response;

          let concatFiles: any[] = [];
          if (temArqBase) {
            concatFiles = [...response.listaArqBase.map((x) => ({...x, fileType: 'base' }))];
          }

          if (temArqPA) {
            concatFiles = [...response.listaArqPA.map((x) => ({...x, fileType: 'preAdm' })), ...concatFiles];
          }

          if (concatFiles.length === 0) {
            toast.error('Não há arquivos no período selecionado. Por favor, selecione outro período.');
            setMrFilesOptions([]);
            return;
          }

          const files = concatFiles.map((file) => ({
            value: String(file.codArq),
            label: file.denominacaoArq,
            fileType: file.fileType as FileType,
          }));

          setMrFilesOptions(files);
        },
        catchMessage: 'Não foi possível buscar os arquivos existentes no período.',
      }));
    } else {
      toast.error('Erro ao escolher período');
    }
  }, [apiCall, selectedCompany.value, selectedReport.value, setIsLoading, userCode, userProfile]);

  const handleSpecificPeriodChangeMrReport = useCallback(async (value: Value) => {
    if (Array.isArray(value)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const adjustedByTimezone = value.map((date) => subHours(date!, 3));
      setSpecificPeriod([adjustedByTimezone[0], adjustedByTimezone[1]]);

      const periodLabel = `De ${format(value[0] as Date, 'dd/MM/yyyy')} até ${format(value[1] as Date, 'dd/MM/yyyy')}`;
      setSpecificPeriodLabel(periodLabel);
    } else {
      toast.error('Erro ao escolher período');
    }
  }, []);

  const reportOptions = useMemo(() => (
    [
      { value: 60, label: 'Pedido SPE - Caixa Pré-Pago (Por Arquivo)' },
      { value: 601, label: 'Pedido SPE - Caixa Pré-Pago (Por Período - Pre Admissional)' },
      ...(routingPermissions?.imprimir ? [
        { value: 61, label: 'Multirotas - Por Periodo' },
        { value: 62, label: 'Multirotas - Por Arquivo' },
        { value: 50, label: 'Otimização - Pré-Admissional (Por Período)' },
        { value: 501, label: 'Otimização - Pré-Admissional (Por Arquivo)' },
        { value: 5011, label: 'Bilhetes VT - Pré-Admissional (Por Período)' },
        { value: 5012, label: 'Bilhetes VT - Pré-Admissional (Por Arquivo)' },
        { value: 5, label: 'Otimização - Base (Por Período)' },
        { value: 502, label: 'Otimização - Base (Por Arquivo)' },
        { value: 5021, label: 'Bilhetes VT - Base (Por Período)' },
        { value: 5022, label: 'Bilhetes VT - Base (Por Arquivo)' },
        { value: 509, label: 'Otimização - Pré-Admissional (Por Período) - BILHETES SEM INTEGRACAO' },
        { value: 5019, label: 'Otimização - Pré-Admissional (Por Arquivo) - BILHETES SEM INTEGRACAO' },
        { value: 59, label: 'Otimização - Base (Por Período) - BILHETES SEM INTEGRACAO' },
        { value: 5029, label: 'Otimização - Base (Por Arquivo) - BILHETES SEM INTEGRACAO' },
        { value: 571, label: 'contestações' },
        { value: 23, label: 'Itens de Vale-Transporte Habilitados' },
        { value: 52, label: 'Consultas Geradas' },
        { value: 503, label: 'Comparativo de Tempo e Distância' },
        { value: 600, label: 'Pedido PLUXEE - Arquivo PLANSIP (Por Arquivo)' },
        { value: 6010, label: 'Pedido PLUXEE - Arquivo PLANSIP (Por Período - Pre Admissional)' },
        { value: 604, label: 'Relatório Tabela Repositória' },
      ] : []),
      ...(userProfile >= 10 ? [] : [
        { value: 55, label: 'Total Geral de Consultas Geradas Pré-Adm e Multi-Rotas' },
        { value: 550, label: 'Total Geral de Consultas Geradas Base' },
        { value: 57, label: 'Contestações (Todas as empresas)' },
        { value: 572, label: 'Relatório de Total de Reclamações por Período - Resultado Sem Abrangência' },
        { value: 504, label: 'Relatório de Estatísticas de Consultas Geradas X Reclamações Geradas' },
      ]),
      ...((canAccessFinancial && userProfile < 10) ? [
        { value: 701, label: 'Controle Mensal de Contas a Receber - Por Período de Geração de Parcelas' },
        { value: 702, label: 'Controle Mensal de Contas a Receber - Por Período de Vencimento de Parcelas' },
        { value: 703, label: 'Controle Mensal de Contas a Receber - Por Período de Geração de Parcelas + Empresa' },
        { value: 521, label: 'Relatório de Faturamento Mensal Detalhado' },
        { value: 704, label: 'RELATÓRIO FINANCEIRO DE EMISSÃO NF - OMIE' },
      ] : [])
    ]
  ), [routingPermissions?.imprimir, userProfile, canAccessFinancial]);

  const reportOptionsWhenDifferentSubdomain = useMemo(() => (
    [
      ...(routingPermissions?.imprimir ? [
        { value: 61, label: 'Multirotas - Por Periodo' },
        { value: 62, label: 'Multirotas - Por Arquivo' },
        { value: 50, label: 'Pré-admissional - avulso (por período)' },
        { value: 501, label: 'Pré-admissional - avulso (por arquivo)' },
        { value: 5011, label: 'Resultado benefícios - avulso (por período)' },
        { value: 5012, label: 'Resultado benefícios - avulso (por arquivo)' },
        { value: 509, label: 'Pré-admissional - avulso (por período) - sem integração tarifa social' },
        { value: 5019, label: 'Pré-admissional - avulso (por arquivo) - sem integração tarifa social' },
        { value: 61, label: 'Multi Rotas (por período)' },
        { value: 62, label: 'Multi Rotas (por arquivo)' },
        { value: 571, label: 'Contestações' },
        { value: 503, label: 'Comparativo de Tempo e Distância' },
        { value: 600, label: 'Pedido PLUXEE - Arquivo PLANSIP (Por Arquivo)' },
        { value: 6010, label: 'Pedido PLUXEE - Arquivo PLANSIP (Por Período - Pre Admissional)' },
        { value: 604, label: 'Relatório Tabela Repositória' },
      ] : [])
    ]
  ), [routingPermissions?.imprimir]);

  const downloadReport = useCallback(async () => {
    const downloadReportLiterals: { [key: number] : () => Promise<void> } = {
      60: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: selectedFileType === 'preAdm' ? 1 : 2,
            valorInt1: selectedFileType === 'preAdm' ? selectedFile.value : 0,
            valorLong: selectedFileType === 'base' ? selectedFile.value : 0,
            tipoRel: selectedReport.value,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel60;

            const mappedReportList = report?.map((rp) => ({
              CNPJ: rp.CNPJ ?? '',
              CEP: rp.CEP ?? '',
              LOGRADOURO: rp.LOGRADOURO ?? '',
              NUMERO: rp.NUMERO ?? '',
              COMPLEMENTO: rp.COMPLEMENTO ?? '',
              PONTO_REFERENCIA: rp.PONTO_REFERENCIA ?? '',
              UF: rp.UF ?? '',
              ESTADO: rp.ESTADO ?? '',
              MATRICULA: rp.MATRICULA ?? '',
              NOME_DO_FUNCIONARIO: rp.NOME_DO_FUNCIONARIO ?? '',
              CPF: rp.CPF ?? '',
              RG: rp.RG ?? '',
              DATA_DE_NASCIMENTO: rp.DATA_DE_NASCIMENTO ?? '',
              CARGO: rp.CARGO ?? '',
              DEPARTAMENTO: rp.DEPARTAMENTO ?? '',
              NOME_DA_MAE: rp.NOME_DA_MAE ?? '',
              BENEFICIO_DO_FUNCIONARIO: rp.BENEFICIO_DO_FUNCIONARIO ?? '',
              VALOR_UNITARIO: rp.VALOR_UNITARIO ?? '',
              QUANTIDADE_DIARIA: rp.QUANTIDADE_DIARIA ?? '',
              PERIODO_DE_DIAS_TRABALHADOS: rp.PERIODO_DE_DIAS_TRABALHADOS ?? '',
              STATUS: rp.STATUS ?? '',
            }));

            ExportXlsx({
              filename: `Pedido SPE - Caixa Pré-Pago - Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: mappedReportList,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar a planilha de otimização.',
        });
      },
      601: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 1,
            valorInt1: 0,
            valorLong: 0,
            tipoRel: 601,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel60;

            const mappedReportList = report?.map((rp) => ({
              CNPJ: rp.CNPJ ?? '',
              CEP: rp.CEP ?? '',
              LOGRADOURO: rp.LOGRADOURO ?? '',
              NUMERO: rp.NUMERO ?? '',
              COMPLEMENTO: rp.COMPLEMENTO ?? '',
              PONTO_REFERENCIA: rp.PONTO_REFERENCIA ?? '',
              UF: rp.UF ?? '',
              ESTADO: rp.ESTADO ?? '',
              MATRICULA: rp.MATRICULA ?? '',
              NOME_DO_FUNCIONARIO: rp.NOME_DO_FUNCIONARIO ?? '',
              CPF: rp.CPF ?? '',
              RG: rp.RG ?? '',
              DATA_DE_NASCIMENTO: rp.DATA_DE_NASCIMENTO ?? '',
              CARGO: rp.CARGO ?? '',
              DEPARTAMENTO: rp.DEPARTAMENTO ?? '',
              NOME_DA_MAE: rp.NOME_DA_MAE ?? '',
              BENEFICIO_DO_FUNCIONARIO: rp.BENEFICIO_DO_FUNCIONARIO ?? '',
              VALOR_UNITARIO: rp.VALOR_UNITARIO ?? '',
              QUANTIDADE_DIARIA: rp.QUANTIDADE_DIARIA ?? '',
              PERIODO_DE_DIAS_TRABALHADOS: rp.PERIODO_DE_DIAS_TRABALHADOS ?? '',
              STATUS: rp.STATUS ?? '',
            }));

            ExportXlsx({
              filename: `Pedido SPE - Caixa Pré-Pago - Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: mappedReportList,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar a planilha de otimização.',
        });
      },
      600: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: selectedFileType === 'preAdm' ? 1 : 2,
            valorInt1: selectedFileType === 'preAdm' ? selectedFile.value : 0,
            valorLong: selectedFileType === 'base' ? selectedFile.value : 0,
            tipoRel: 60,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel60;

            const groupedByCpf = report.reduce((acc, curr) => {
              const cpf = curr.CPF;
              if (!acc[cpf]) {
                acc[cpf] = [];
              }
              acc[cpf].push(curr);
              return acc;
            }, {} as Record<string, typeof report>);

            const toArray = Object.entries(groupedByCpf);
            const maxItemsCount = Math.max(...Object.values(groupedByCpf).map(items => items.length));
            const finalGrouping = toArray.map(([, data]) => {
              return {
                'NOME DO BENEFICIÁRIO': data[0].NOME_DO_FUNCIONARIO ?? '',
                CPF: data[0].CPF ?? '',
                'DATA DE NASC.': data[0].DATA_DE_NASCIMENTO ?? '',
                'NÚMERO DO RG': data[0].RG ?? '',
                'NOME DA MÃE ': data[0].NOME_DA_MAE ?? '',
                'NOME DEPART.': data[0].DEPARTAMENTO ?? '',
                'RESPONSÁVEL Pelo Depart.': '',
                'Tipo de Logradouro - rua, av, estr, rod TRABALHO': '',
                'Descrição do logradouro - nome da rua TRABALHO': data[0].LOGRADOURO ?? '',
                'Número TRABALHO': data[0].NUMERO ?? '',
                'Complemento TRABALHO': data[0].COMPLEMENTO ?? '',
                'Bairro TRABALHO': data[0].LT_BAIRRO ?? '',
                'CIDADE TRABALHO': data[0].LT_CIDADE ?? '',
                'UF TRABALHO': data[0].UF ?? '',
                'CEP TRABALHO': data[0].CEP ?? '',
                'QTD. DIAS ÚTEIS': data[0].PERIODO_DE_DIAS_TRABALHADOS ?? '',
                ...Array.from({ length: maxItemsCount }, (_, index) => data[index]).reduce((acc, item, index) => ({
                  ...acc,
                  [`COD. OPERADORA ${index + 1}`]: item?.BENEFICIO_DO_FUNCIONARIO?.split('|')[0] ?? '',
                  [`COD. LINHA ${index + 1}`]: item?.BENEFICIO_DO_FUNCIONARIO?.split('|')[1] ?? '',
                  [`QTD. PASSES POR DIA / COTAS (SPTrans) ${index + 1}`]: item?.QUANTIDADE_DIARIA,
                  [`VALOR DO BILHETE ${index + 1}`]: item?.VALOR_UNITARIO,
                  [`NÚMERO CARTÃO ${index + 1}`]: '',
                }), {}),
                'Tipo de Logradouro - rua, av, estr, rod': '',
                'Descrição do logradouro - nome da rua': data[0].LOGRADOURO ?? '',
                'Número': data[0].FUNC_NUMERO ?? '',
                'Complemento': data[0].FUNC_COMPLEMENTO ?? '',
                'Bairro': data[0].FUNC_BAIRRO ?? '',
                'CIDADE': data[0].FUNC_CIDADE ?? '',
                'UF': data[0].FUNC_UF ?? '',
                'CEP': data[0].FUNC_CEP ?? '',
                STATUS: data[0].STATUS ?? '',
              };
            });

            ExportXlsx({
              filename: `Pedido PLUXEE - Arquivo PLANSIP Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: finalGrouping,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar a planilha de otimização.',
        });
      },
      6010: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 1,
            valorInt1: 0,
            valorLong: 0,
            tipoRel: 601,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel60;

            const groupedByCpf = report.reduce((acc, curr) => {
              const cpf = curr.CPF;
              if (!acc[cpf]) {
                acc[cpf] = [];
              }
              acc[cpf].push(curr);
              return acc;
            }, {} as Record<string, typeof report>);

            const toArray = Object.entries(groupedByCpf);
            const maxItemsCount = Math.max(...Object.values(groupedByCpf).map(items => items.length));
            const finalGrouping = toArray.map(([, data]) => {
              return {
                'NOME DO BENEFICIÁRIO': data[0].NOME_DO_FUNCIONARIO ?? '',
                CPF: data[0].CPF ?? '',
                'DATA DE NASC.': data[0].DATA_DE_NASCIMENTO ?? '',
                'NÚMERO DO RG': data[0].RG ?? '',
                'NOME DA MÃE ': data[0].NOME_DA_MAE ?? '',
                'NOME DEPART.': data[0].DEPARTAMENTO ?? '',
                'RESPONSÁVEL Pelo Depart.': '',
                'Tipo de Logradouro - rua, av, estr, rod TRABALHO': '',
                'Descrição do logradouro - nome da rua TRABALHO': data[0].LOGRADOURO ?? '',
                'Número TRABALHO': data[0].NUMERO ?? '',
                'Complemento TRABALHO': data[0].COMPLEMENTO ?? '',
                'Bairro TRABALHO': data[0].LT_BAIRRO ?? '',
                'CIDADE TRABALHO': data[0].LT_CIDADE ?? '',
                'UF TRABALHO': data[0].UF ?? '',
                'CEP TRABALHO': data[0].CEP ?? '',
                'QTD. DIAS ÚTEIS': data[0].PERIODO_DE_DIAS_TRABALHADOS ?? '',
                ...Array.from({ length: maxItemsCount }, (_, index) => data[index]).reduce((acc, item, index) => ({
                  ...acc,
                  [`COD. OPERADORA ${index + 1}`]: item?.BENEFICIO_DO_FUNCIONARIO?.split('|')[0] ?? '',
                  [`COD. LINHA ${index + 1}`]: item?.BENEFICIO_DO_FUNCIONARIO?.split('|')[1] ?? '',
                  [`QTD. PASSES POR DIA / COTAS (SPTrans) ${index + 1}`]: item?.QUANTIDADE_DIARIA,
                  [`VALOR DO BILHETE ${index + 1}`]: item?.VALOR_UNITARIO,
                  [`NÚMERO CARTÃO ${index + 1}`]: '',
                }), {}),
                'Tipo de Logradouro - rua, av, estr, rod': '',
                'Descrição do logradouro - nome da rua': data[0].LOGRADOURO ?? '',
                'Número': data[0].FUNC_NUMERO ?? '',
                'Complemento': data[0].FUNC_COMPLEMENTO ?? '',
                'Bairro': data[0].FUNC_BAIRRO ?? '',
                'CIDADE': data[0].FUNC_CIDADE ?? '',
                'UF': data[0].FUNC_UF ?? '',
                'CEP': data[0].FUNC_CEP ?? '',
                STATUS: data[0].STATUS ?? '',
              };
            });

            ExportXlsx({
              filename: `Pedido PLUXEE - Arquivo PLANSIP Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: finalGrouping,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar a planilha de otimização.',
        });
      },
      604: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorLong: 0,
            tipoRel: 604,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados para o relatório (${response.msg})`);
              return;
            }

            const report = response.rel604 as unknown as { [key: string]: string | number }[];

            ExportXlsx({
              filename: `Relatório Tabela Repositória - Emp_${selectedCompany.value}_${selectedCompany.label}`,
              data: report,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      503: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            valorLong1: 0,
            valorLong2: 0,
            valorString: '',
            valorString1: '',
            valorString2: '',
            tipoRel: 503,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel503 as unknown as { [key: string]: string | number }[];

            ExportXlsx({
              filename: `Relatório Comparativo de Tempo e Distância - Empresa_${selectedCompany.value}_${selectedCompany.label}_${getCurrentDateWithHyphen()} ${specificPeriodLabel}`,
              data: report,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar a planilha de otimização.',
        });
      },
      61: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorLong: 0,
            tipoRel: 61,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel61 as unknown as { [key: string]: string | number }[];

            ExportXlsx({
              filename: `Relatorio Multi-Rotas por periodo - Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: report,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      62: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorLong: selectedFile.value,
            tipoRel: 62,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel61 as unknown as { [key: string]: string | number }[];

            ExportXlsx({
              filename: `Relatorio Multi-Rotas por periodo - Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: report,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      501: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: selectedFile.value,
            valorInt1: 0,
            valorLong: 0,
            tipoRel: 501,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel50 as unknown as { [key: string]: string | number }[];
            const mappedReportList = report?.map((rp) => ({
              CodFunc: rp.codFunc ?? '',
              CodConsulta: rp.codConsulta ?? '',
              CNPJ: rp.cnpj ?? '',
              Empresa: rp.empresa ?? '',
              CodLocal: rp.codLTAntes ?? '',
              DescLocal: rp.localTrabAntes ?? '',
              CodLocalDepois: rp.codLTAtual ?? '',
              DescLocalDepois: rp.localTrabAtual ?? '',
              CPF: rp.cpf ?? '',
              Matricula: rp.matricula ?? '',
              Nome: rp.nome ?? '',
              Logradouro: rp.funcLogradouro ?? '',
              Numero: rp.funcEndNum ?? '',
              Complem: rp.funcComplemento ?? '',
              'CEP Antes': rp.funcCEPAntes ?? '',
              'CEP Depois': rp.funcCEPAtual ?? '',
              'CEP DIF': onlyNumbers(String(rp.funcCEPAntes)) - onlyNumbers(String(rp.funcCEPAtual)),
              Bairro: rp.funcBairro ?? '',
              Cidade: rp.funcCidade ?? '',
              UF: rp.funcUF ?? '',
              'E-mail': rp.mail ?? '',
              HoraEntra: rp.horaEntra ?? '',
              HoraSai: rp.horaSai ?? '',
              TurnoDias: rp.TurnoDias ?? '',
              'Valor Dia': rp.valorVTRot ?? '',
              'Valor Mês': rp.valorVTRotMes ?? '',
              'StatusResultado': rp.statusResultado ?? '',
              'StatusConsulta': rp.statusConsulta ?? '',
              'CanceladoVT':  rp.canceladoVT ? 'SIM' : 'NÃO',
              'Mudança Cadastral': rp.mudouCadastro,
              'DataCadastro': String(rp.dataCadastro).includes('1900') ? '' : format(new Date(rp.dataCadastro), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              DataCadAtualizado: String(rp.dataCadAtualizado).includes('1900') ? '' : format(new Date(rp.dataCadAtualizado), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              'Data Inicial IA': String(rp.Data_Inicial_IA).includes('1900') ? '' : rp.Data_Inicial_IA,
              'Ultimo Envio IA': String(rp.Ultimo_Envio_IA).includes('1900') ? '' : rp.Ultimo_Envio_IA,
              Canal: rp.canal ?? '',
              InicioApp: String(rp.inicioApp).includes('1900') ? '' : format(new Date(rp.inicioApp), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              SituacaoCadastro: rp.situacaoCadastro ?? '',
              DataImplantacao: String(rp.dataImplantacao).includes('1900') ? '' : format(new Date(rp.dataImplantacao), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              GeradoPorBiasTag: rp.geradoPorBiasTag || 'Não especificado',
              CompraVT: rp.compraVT ?? '',
              CodVT1: rp.r1VT1_CodVT ?? '',
              DescVT1: rp.r1VT1_Descr ?? '',
              QtdaVT1: rp.r1VT1_Qtd ?? '',
              VlrVT1: rp.r1VT1_Tarifa ?? '',
              CodVT2: rp.r1VT2_CodVT ?? '',
              DescVT2: rp.r1VT2_Descr ?? '',
              QtdaVT2: rp.r1VT2_Qtd ?? '',
              VlrVT2: rp.r1VT2_Tarifa ?? '',
              CodVT3: rp.r1VT3_CodVT ?? '',
              DescVT3: rp.r1VT3_Descr ?? '',
              QtdaVT3: rp.r1VT3_Qtd ?? '',
              VlrVT3: rp.r1VT3_Tarifa ?? '',
              CodVT4: rp.r1VT4_CodVT ?? '',
              DescVT4: rp.r1VT4_Descr ?? '',
              QtdaVT4: rp.r1VT4_Qtd ?? '',
              VlrVT4: rp.r1VT4_Tarifa ?? '',
              CodVT5: rp.r1VT5_CodVT ?? '',
              DescVT5: rp.r1VT5_Descr ?? '',
              QtdaVT5: rp.r1VT5_Qtd ?? '',
              VlrVT5: rp.r1VT5_Tarifa ?? '',
              'Cartao URBS-PR': rp.Cartao_URBS_PR ?? '',
              'Dist a Pé': rp.distaciaPe ?? '',
              'DistOnibus_Ida': rp.distOnibus_Ida ?? '',
              'DistOnibus_Volta': rp.distOnibus_Volta ?? '',
              'DistTrem_Ida': rp.distTrem_Ida ?? '',
              'DistTrem_Volta': rp.distTrem_Volta ?? '',
            }));

            ExportXlsx({
              filename: `Relatorio Otimização Pre Adm por arquivo - Emp_${selectedCompany.value}_${selectedCompany.label} ${selectedFile.label}`,
              data: mappedReportList,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      5019: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: selectedFile.value,
            valorInt1: 1,
            valorLong: 0,
            tipoRel: 501,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel50 as unknown as { [key: string]: string | number }[];
            const mappedReportList = report?.map((rp) => ({
              CodFunc: rp.codFunc ?? '',
              CodConsulta: rp.codConsulta ?? '',
              CNPJ: rp.cnpj ?? '',
              Empresa: rp.empresa ?? '',
              CodLocal: rp.codLTAntes ?? '',
              DescLocal: rp.localTrabAntes ?? '',
              CodLocalDepois: rp.codLTAtual ?? '',
              DescLocalDepois: rp.localTrabAtual ?? '',
              CPF: rp.cpf ?? '',
              Matricula: rp.matricula ?? '',
              Nome: rp.nome ?? '',
              Logradouro: rp.funcLogradouro ?? '',
              Numero: rp.funcEndNum ?? '',
              Complem: rp.funcComplemento ?? '',
              'CEP Antes': rp.funcCEPAntes ?? '',
              'CEP Depois': rp.funcCEPAtual ?? '',
              'CEP DIF': onlyNumbers(String(rp.funcCEPAntes)) - onlyNumbers(String(rp.funcCEPAtual)),
              Bairro: rp.funcBairro ?? '',
              Cidade: rp.funcCidade ?? '',
              UF: rp.funcUF ?? '',
              'E-mail': rp.mail ?? '',
              HoraEntra: rp.horaEntra ?? '',
              HoraSai: rp.horaSai ?? '',
              TurnoDias: rp.TurnoDias ?? '',
              'Valor Dia': rp.valorVTRot ?? '',
              'Valor Mês': rp.valorVTRotMes ?? '',
              'StatusResultado': rp.statusResultado ?? '',
              'StatusConsulta': rp.statusConsulta ?? '',
              'CanceladoVT':  rp.canceladoVT ? 'SIM' : 'NÃO',
              'Mudança Cadastral': rp.mudouCadastro,
              'DataCadastro': String(rp.dataCadastro).includes('1900') ? '' : format(new Date(rp.dataCadastro), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              DataCadAtualizado: String(rp.dataCadAtualizado).includes('1900') ? '' : format(new Date(rp.dataCadAtualizado), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              'Data Inicial IA': String(rp.Data_Inicial_IA).includes('1900') ? '' : rp.Data_Inicial_IA,
              'Ultimo Envio IA': String(rp.Ultimo_Envio_IA).includes('1900') ? '' : rp.Ultimo_Envio_IA,
              Canal: rp.canal ?? '',
              InicioApp: String(rp.inicioApp).includes('1900') ? '' : format(new Date(rp.inicioApp), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              SituacaoCadastro: rp.situacaoCadastro ?? '',
              DataImplantacao: String(rp.dataImplantacao).includes('1900') ? '' : format(new Date(rp.dataImplantacao), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              CompraVT: rp.compraVT ?? '',
              CodVT1: rp.r1VT1_CodVT ?? '',
              DescVT1: rp.r1VT1_Descr ?? '',
              QtdaVT1: rp.r1VT1_Qtd ?? '',
              VlrVT1: rp.r1VT1_Tarifa ?? '',
              CodVT2: rp.r1VT2_CodVT ?? '',
              DescVT2: rp.r1VT2_Descr ?? '',
              QtdaVT2: rp.r1VT2_Qtd ?? '',
              VlrVT2: rp.r1VT2_Tarifa ?? '',
              CodVT3: rp.r1VT3_CodVT ?? '',
              DescVT3: rp.r1VT3_Descr ?? '',
              QtdaVT3: rp.r1VT3_Qtd ?? '',
              VlrVT3: rp.r1VT3_Tarifa ?? '',
              CodVT4: rp.r1VT4_CodVT ?? '',
              DescVT4: rp.r1VT4_Descr ?? '',
              QtdaVT4: rp.r1VT4_Qtd ?? '',
              VlrVT4: rp.r1VT4_Tarifa ?? '',
              CodVT5: rp.r1VT5_CodVT ?? '',
              DescVT5: rp.r1VT5_Descr ?? '',
              QtdaVT5: rp.r1VT5_Qtd ?? '',
              VlrVT5: rp.r1VT5_Tarifa ?? '',
              'Cartao URBS-PR': rp.Cartao_URBS_PR ?? '',
              'Dist a Pé': rp.distaciaPe ?? '',
              'DistOnibus_Ida': rp.distOnibus_Ida ?? '',
              'DistOnibus_Volta': rp.distOnibus_Volta ?? '',
              'DistTrem_Ida': rp.distTrem_Ida ?? '',
              'DistTrem_Volta': rp.distTrem_Volta ?? '',
            }));

            ExportXlsx({
              filename: `Relatorio Otimização Pre Adm por arquivo - BILHETES SEM INTEGRACAO - Emp_${selectedCompany.value}_${selectedCompany.label} ${selectedFile.label}`,
              data: mappedReportList,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      5012: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: selectedFile.value,
            valorInt1: 0,
            valorLong: 0,
            tipoRel: 501,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const mappedData = response.rel50.map((rel) => ({
              COD_CONSULTA: rel.codConsulta,
              DATA_ROTEIRIZACAO: format(new Date(rel.dataCadastro), 'dd/MM/yyyy HH:mm'),
              CPF: rel.cpf,
              MATRICULA: rel.matricula,
              NOME: rel.nome,
              COMPRA_VT: rel.statusConsulta.toUpperCase(),
              DATA_IMPLANTACAO: format(new Date(rel.dataImplantacao), 'dd/MM/yyyy HH:mm'),
              CANAL: rel.canal,
              VTS: [
                { VT_COD: rel.r1VT1_CodVT, VT_DESCR: rel.r1VT1_Descr, VT_TARIFA: rel.r1VT1_Tarifa, VT_QTD: rel.r1VT1_Qtd || 1 },
                { VT_COD: rel.r1VT2_CodVT, VT_DESCR: rel.r1VT2_Descr, VT_TARIFA: rel.r1VT2_Tarifa, VT_QTD: rel.r1VT2_Qtd || 1 },
                { VT_COD: rel.r1VT3_CodVT, VT_DESCR: rel.r1VT3_Descr, VT_TARIFA: rel.r1VT3_Tarifa, VT_QTD: rel.r1VT3_Qtd || 1 },
                { VT_COD: rel.r1VT4_CodVT, VT_DESCR: rel.r1VT4_Descr, VT_TARIFA: rel.r1VT4_Tarifa, VT_QTD: rel.r1VT4_Qtd || 1 },
                { VT_COD: rel.r1VT5_CodVT, VT_DESCR: rel.r1VT5_Descr, VT_TARIFA: rel.r1VT5_Tarifa, VT_QTD: rel.r1VT5_Qtd || 1 }
              ]
            }));

            const arrayWithEachVtPerRow = mappedData.reduce<VT[]>((acc, data) => {
              const validVts = data.VTS.filter(vt => vt.VT_COD); // Filtra apenas os objetos com VT_COD truthy
              validVts.forEach(vt => acc.push({
                COD_EMPRESA: String(selectedCompany.value),
                MATRICULA: String(data.MATRICULA),
                CPF: data.CPF,
                NOME: data.NOME,
                COMPRA_VT: data.COMPRA_VT,
                ...vt
              })); // Adiciona ao array final
              return acc;
            }, []);

            ExportXlsx({
              filename: `Planilha de VT - Emp_${selectedCompany.value}_${selectedCompany.label} ${selectedFile.label}`,
              data: arrayWithEachVtPerRow as unknown as { [key: string]: string | number | Date; }[],
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      502: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorLong: selectedFile.value,
            tipoRel: 502,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel5 as unknown as { [key: string]: string | number }[];
            const mappedReportList = report?.map((rp) => ({
              CodFunc: rp.codFunc ?? '',
              CodConsulta: rp.codConsulta ?? '',
              CNPJ: rp.cnpj ?? '',
              Empresa: rp.empresa ?? '',
              CodLocal: rp.codLTArq ?? '',
              DescLocal: rp.localTrabArq ?? '',
              CodLocalAtual: rp.codLTAtual ?? '',
              DescLocalAtual: rp.localTrabAtual ?? '',
              CPF: rp.cpf ?? '',
              Matricula: rp.matricula ?? '',
              Nome: rp.nome ?? '',
              Logradouro: rp.funcLogradouro ?? '',
              Numero: rp.funcEndNum ?? '',
              Complem: rp.funcComplemento ?? '',
              CEPAntes: rp.funcCEPArq ?? '',
              CEPAtual: rp.funcCEPAtual ?? '',
              DifCEP: rp.diferencaCEP ?? '',
              Bairro: rp.funcBairro ?? '',
              Cidade: rp.funcCidade ?? '',
              UF: rp.funcUF ?? '',
              'E-mail': rp.mail ?? '',
              HoraEntra: rp.horaEntra ?? '',
              HoraSai: rp.horaSai ?? '',
              TurnoDias: rp.TurnoDias ?? '',
              'Valor Dia Atual': rp.valorVTInicial ?? '',
              'Valor Mês Atual': rp.valorVTInicialMes ?? '',
              'Valor dia Rot': rp.valorVTRot ?? '',
              'Valor Mês Rot': rp.valorVTRotMes ?? '',
              'Otimiz Dia': rp.otimizacaoDia ?? '',
              'Otimiz Mês': rp.otimizacaoMes ?? '',
              'Tem GS': rp.gestaoSaldo,
              'StatusResultado': rp.statusResultado ?? '',
              'StatusConsulta': rp.statusConsulta ?? '',
              'CanceladoVT': rp.canceladoVT ? 'SIM' : 'NÃO',
              'MudouCadastro': rp.mudouCadastro ? 'SIM' : 'NÃO',
              'DataCadastro': String(rp.dataCadastro).includes('1900') ? '' : format(new Date(rp.dataCadastro), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              DataCadAtualizado: String(rp.dataCadAtualizado).includes('1900') ? '' : format(new Date(rp.dataCadAtualizado), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              'Data Inicial IA': String(rp.Data_Inicial_IA).includes('1900') ? '' : rp.Data_Inicial_IA,
              'Ultimo Envio IA': String(rp.Ultimo_Envio_IA).includes('1900') ? '' : rp.Ultimo_Envio_IA,
              Canal: rp.canal ?? '',
              InicioApp: String(rp.inicioApp).includes('1900') ? '' : format(new Date(rp.inicioApp), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              SituacaoCadastro: rp.situacaoCadastro ?? '',
              DataImplantacao: String(rp.dataImplantacao).includes('1900') ? '' : format(new Date(rp.dataImplantacao), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              GeradoPorBiasTag: rp.geradoPorBiasTag || 'Não especificado',
              CompraVT: rp.compraVT ?? '',
              CodVT1: rp.r1VT1_CodVT ?? '',
              DescVT1: rp.r1VT1_Descr ?? '',
              QtdaVT1: rp.r1VT1_Qtd ?? '',
              VlrVT1: rp.r1VT1_Tarifa ?? '',
              CodVT2: rp.r1VT2_CodVT ?? '',
              DescVT2: rp.r1VT2_Descr ?? '',
              QtdaVT2: rp.r1VT2_Qtd ?? '',
              VlrVT2: rp.r1VT2_Tarifa ?? '',
              CodVT3: rp.r1VT3_CodVT ?? '',
              DescVT3: rp.r1VT3_Descr ?? '',
              QtdaVT3: rp.r1VT3_Qtd ?? '',
              VlrVT3: rp.r1VT3_Tarifa ?? '',
              CodVT4: rp.r1VT4_CodVT ?? '',
              DescVT4: rp.r1VT4_Descr ?? '',
              QtdaVT4: rp.r1VT4_Qtd ?? '',
              VlrVT4: rp.r1VT4_Tarifa ?? '',
              CodVT5: rp.r1VT5_CodVT ?? '',
              DescVT5: rp.r1VT5_Descr ?? '',
              QtdaVT5: rp.r1VT5_Qtd ?? '',
              VlrVT5: rp.r1VT5_Tarifa ?? '',
              'Cartao URBS-PR': rp.Cartao_URBS_PR ?? '',
              'Dist a Pé': rp.distaciaPe ?? '',
              'DistOnibus_Ida': rp.distOnibus_Ida ?? '',
              'DistOnibus_Volta': rp.distOnibus_Volta ?? '',
              'DistTrem_Ida': rp.distTrem_Ida ?? '',
              'DistTrem_Volta': rp.distTrem_Volta ?? '',
            }));

            ExportXlsx({
              filename: `Relatorio Otimização Base por arquivo - Emp_${selectedCompany.value}_${selectedCompany.label} ${selectedFile.label}`,
              data: mappedReportList,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      5029: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 1,
            valorLong: selectedFile.value,
            tipoRel: 502,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel5 as unknown as { [key: string]: string | number }[];
            const mappedReportList = report?.map((rp) => ({
              CodFunc: rp.codFunc ?? '',
              CodConsulta: rp.codConsulta ?? '',
              CNPJ: rp.cnpj ?? '',
              Empresa: rp.empresa ?? '',
              CodLocal: rp.codLTArq ?? '',
              DescLocal: rp.localTrabArq ?? '',
              CodLocalAtual: rp.codLTAtual ?? '',
              DescLocalAtual: rp.localTrabAtual ?? '',
              CPF: rp.cpf ?? '',
              Matricula: rp.matricula ?? '',
              Nome: rp.nome ?? '',
              Logradouro: rp.funcLogradouro ?? '',
              Numero: rp.funcEndNum ?? '',
              Complem: rp.funcComplemento ?? '',
              CEPAntes: rp.funcCEPArq ?? '',
              CEPAtual: rp.funcCEPAtual ?? '',
              DifCEP: rp.diferencaCEP ?? '',
              Bairro: rp.funcBairro ?? '',
              Cidade: rp.funcCidade ?? '',
              UF: rp.funcUF ?? '',
              'E-mail': rp.mail ?? '',
              HoraEntra: rp.horaEntra ?? '',
              HoraSai: rp.horaSai ?? '',
              TurnoDias: rp.TurnoDias ?? '',
              'Valor Dia Atual': rp.valorVTInicial ?? '',
              'Valor Mês Atual': rp.valorVTInicialMes ?? '',
              'Valor dia Rot': rp.valorVTRot ?? '',
              'Valor Mês Rot': rp.valorVTRotMes ?? '',
              'Otimiz Dia': rp.otimizacaoDia ?? '',
              'Otimiz Mês': rp.otimizacaoMes ?? '',
              'Tem GS': rp.gestaoSaldo,
              'StatusResultado': rp.statusResultado ?? '',
              'StatusConsulta': rp.statusConsulta ?? '',
              'CanceladoVT': rp.canceladoVT ? 'SIM' : 'NÃO',
              'MudouCadastro': rp.mudouCadastro ? 'SIM' : 'NÃO',
              'DataCadastro': String(rp.dataCadastro).includes('1900') ? '' : format(new Date(rp.dataCadastro), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              DataCadAtualizado: String(rp.dataCadAtualizado).includes('1900') ? '' : format(new Date(rp.dataCadAtualizado), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              'Data Inicial IA': String(rp.Data_Inicial_IA).includes('1900') ? '' : rp.Data_Inicial_IA,
              'Ultimo Envio IA': String(rp.Ultimo_Envio_IA).includes('1900') ? '' : rp.Ultimo_Envio_IA,
              Canal: rp.canal ?? '',
              InicioApp: String(rp.inicioApp).includes('1900') ? '' : format(new Date(rp.inicioApp), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              SituacaoCadastro: rp.situacaoCadastro ?? '',
              DataImplantacao: String(rp.dataImplantacao).includes('1900') ? '' : format(new Date(rp.dataImplantacao), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              CompraVT: rp.compraVT ?? '',
              CodVT1: rp.r1VT1_CodVT ?? '',
              DescVT1: rp.r1VT1_Descr ?? '',
              QtdaVT1: rp.r1VT1_Qtd ?? '',
              VlrVT1: rp.r1VT1_Tarifa ?? '',
              CodVT2: rp.r1VT2_CodVT ?? '',
              DescVT2: rp.r1VT2_Descr ?? '',
              QtdaVT2: rp.r1VT2_Qtd ?? '',
              VlrVT2: rp.r1VT2_Tarifa ?? '',
              CodVT3: rp.r1VT3_CodVT ?? '',
              DescVT3: rp.r1VT3_Descr ?? '',
              QtdaVT3: rp.r1VT3_Qtd ?? '',
              VlrVT3: rp.r1VT3_Tarifa ?? '',
              CodVT4: rp.r1VT4_CodVT ?? '',
              DescVT4: rp.r1VT4_Descr ?? '',
              QtdaVT4: rp.r1VT4_Qtd ?? '',
              VlrVT4: rp.r1VT4_Tarifa ?? '',
              CodVT5: rp.r1VT5_CodVT ?? '',
              DescVT5: rp.r1VT5_Descr ?? '',
              QtdaVT5: rp.r1VT5_Qtd ?? '',
              VlrVT5: rp.r1VT5_Tarifa ?? '',
              'Cartao URBS-PR': rp.Cartao_URBS_PR ?? '',
              'Dist a Pé': rp.distaciaPe ?? '',
              'DistOnibus_Ida': rp.distOnibus_Ida ?? '',
              'DistOnibus_Volta': rp.distOnibus_Volta ?? '',
              'DistTrem_Ida': rp.distTrem_Ida ?? '',
              'DistTrem_Volta': rp.distTrem_Volta ?? '',
            }));

            ExportXlsx({
              filename: `Relatorio Otimização Base por arquivo - BILHETES SEM INTEGRACAO - Emp_${selectedCompany.value}_${selectedCompany.label} ${selectedFile.label}`,
              data: mappedReportList,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      5022: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorLong: selectedFile.value,
            tipoRel: 502,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const mappedData = response.rel50.map((rel) => ({
              COD_CONSULTA: rel.codConsulta,
              DATA_ROTEIRIZACAO: format(new Date(rel.dataCadastro), 'dd/MM/yyyy HH:mm'),
              CPF: rel.cpf,
              MATRICULA: rel.matricula,
              NOME: rel.nome,
              COMPRA_VT: rel.statusConsulta.toUpperCase(),
              DATA_IMPLANTACAO: format(new Date(rel.dataImplantacao), 'dd/MM/yyyy HH:mm'),
              CANAL: rel.canal,
              VTS: [
                { VT_COD: rel.r1VT1_CodVT, VT_DESCR: rel.r1VT1_Descr, VT_TARIFA: rel.r1VT1_Tarifa, VT_QTD: rel.r1VT1_Qtd || 1 },
                { VT_COD: rel.r1VT2_CodVT, VT_DESCR: rel.r1VT2_Descr, VT_TARIFA: rel.r1VT2_Tarifa, VT_QTD: rel.r1VT2_Qtd || 1 },
                { VT_COD: rel.r1VT3_CodVT, VT_DESCR: rel.r1VT3_Descr, VT_TARIFA: rel.r1VT3_Tarifa, VT_QTD: rel.r1VT3_Qtd || 1 },
                { VT_COD: rel.r1VT4_CodVT, VT_DESCR: rel.r1VT4_Descr, VT_TARIFA: rel.r1VT4_Tarifa, VT_QTD: rel.r1VT4_Qtd || 1 },
                { VT_COD: rel.r1VT5_CodVT, VT_DESCR: rel.r1VT5_Descr, VT_TARIFA: rel.r1VT5_Tarifa, VT_QTD: rel.r1VT5_Qtd || 1 }
              ]
            }));

            const arrayWithEachVtPerRow = mappedData.reduce<VT[]>((acc, data) => {
              const validVts = data.VTS.filter(vt => vt.VT_COD); // Filtra apenas os objetos com VT_COD truthy
              validVts.forEach(vt => acc.push({
                COD_EMPRESA: String(selectedCompany.value),
                MATRICULA: String(data.MATRICULA),
                CPF: data.CPF,
                NOME: data.NOME,
                COMPRA_VT: data.COMPRA_VT,
                ...vt
              })); // Adiciona ao array final
              return acc;
            }, []);

            ExportXlsx({
              filename: `Planilha de VT - Emp_${selectedCompany.value}_${selectedCompany.label} ${selectedFile.label}`,
              data: arrayWithEachVtPerRow as unknown as { [key: string]: string | number | Date; }[],
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      52: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: isCustomer ? usersCompanyCode : generatedConsultsReportType === 'byCompany' ? selectedCompany.value : 0,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: isCustomer ? 1 : generatedConsultsReportType === 'all' ? 2 : (generatedConsultsReportType === 'byCompany' ? 1 : 3),
            valorInt1: periodBeingSelected.month,
            valorInt2: periodBeingSelected.year,
            valorLong: isCustomer ? 0 : generatedConsultsReportType === 'byPartner' ? selectedPartner.value : 0,
            tipoRel: 52,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel52 as unknown as { [key: string]: string | number }[];

            const fileNameLiterals: { [key: string]: string } = {
              'all': `Consultas Geradas_Todas_Empresas_Periodo_${periodBeingSelected.month}-${periodBeingSelected.year}_Em ${new Date().toISOString()}`,
              'byPartner': `Consultas Geradas_Todas_Empresas_por_Parceiro_${selectedPartner.label} e_Periodo_${periodBeingSelected.month}-${periodBeingSelected.year}_Em ${new Date().toISOString()}`,
              'byCompany': `Consultas Geradas_Emp_${selectedCompany.value}_${selectedCompany.label}_Periodo_${periodBeingSelected.month}-${periodBeingSelected.year}_Em ${new Date().toISOString()}`,
            };

            ExportXlsx({
              filename: fileNameLiterals[generatedConsultsReportType],
              data: report,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      55: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: 0,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: generatedConsultsReportType === 'all' ? 1 : 2,
            valorInt1: periodBeingSelected.month,
            valorInt2: periodBeingSelected.year,
            valorLong: generatedConsultsReportType === 'byPartner' ? selectedPartner.value : 0,
            tipoRel: 55,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel55 as unknown as { [key: string]: string | number }[];

            const fileNameLiterals: { [key: string]: string } = {
              'all': `Total Geral de Consultas Geradas por Mes-Ano_${periodBeingSelected.month}-${periodBeingSelected.year}_Em ${new Date().toISOString()}`,
              'byPartner': `Total Geral de Consultas Geradas_Parceiro ${selectedPartner.label}_e Mes-Ano_${periodBeingSelected.month}-${periodBeingSelected.year}_Em ${new Date().toISOString()}`,
            };

            ExportXlsx({
              filename: fileNameLiterals[generatedConsultsReportType],
              data: report,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      550: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: 0,
            data1: new Date().toISOString().split('T')[0],
            data2: new Date().toISOString().split('T')[0],
            valorInt: generatedConsultsReportType === 'all' ? 1 : 2,
            valorInt1: periodBeingSelected.month,
            valorInt2: periodBeingSelected.year,
            valorLong: generatedConsultsReportType === 'byPartner' ? selectedPartner.value : 0,
            tipoRel: 550,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel55 as unknown as { [key: string]: string | number }[];

            const fileNameLiterals: { [key: string]: string } = {
              'all': `Total Geral de Consultas Geradas por Mes-Ano_${periodBeingSelected.month}-${periodBeingSelected.year}_Em ${new Date().toISOString()}`,
              'byPartner': `Total Geral de Consultas Geradas_Parceiro ${selectedPartner.label}_e Mes-Ano_${periodBeingSelected.month}-${periodBeingSelected.year}_Em ${new Date().toISOString()}`,
            };

            ExportXlsx({
              filename: fileNameLiterals[generatedConsultsReportType],
              data: report,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      57: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 57,
          },
          actionAfterResponse: (apiResponse: IReportApiResponse) => {
            if (!apiResponse.codigo) {
              toast.error(`Não foi possível baixar a planilha (${apiResponse.msg})`);
              return;
            }
            if (!apiResponse.rel57 || apiResponse.rel57.length === 0) {
              toast.error(`Não há dados no período selecionado (${apiResponse.msg})`);
              return;
            }

            const report = apiResponse.rel57 as unknown as { [key: string]: string | number }[];
            const mappedReport = report.map((item) => {
              if (String(item.Data_da_Solução).includes('1900')) {
                item.Data_da_Solução = 'Sem Data';
              }
              return item;
            });

            ExportXlsx({
              filename: `Relatório contestações - ${specificPeriodLabel}`,
              data: mappedReport,
            });
          },
          catchMessage: 'Não foi possível baixar o relatório',
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
        });
      },
      571: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 571,
          },
          actionAfterResponse: (apiResponse: IReportApiResponse) => {
            if (!apiResponse.codigo) {
              toast.error(`Não foi possível baixar a planilha (${apiResponse.msg})`);
              return;
            }
            if (!apiResponse.rel57 || apiResponse.rel57.length === 0) {
              toast.error(`Não há dados no período selecionado (${apiResponse.msg})`);
              return;
            }

            const report = apiResponse.rel57 as unknown as { [key: string]: string | number }[];
            const mappedReport = report.map((item) => {
              if (String(item.Data_da_Solução).includes('1900')) {
                item.Data_da_Solução = 'Sem Data';
              }
              return item;
            });

            ExportXlsx({
              filename: `Relatório contestações - Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: mappedReport,
            });
          },
          catchMessage: 'Não foi possível baixar o relatório',
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
        });
      },
      504: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 504,
          },
          actionAfterResponse: (apiResponse: IReportApiResponse) => {
            if (!apiResponse.codigo) {
              toast.error(`Não foi possível baixar a planilha (${apiResponse.msg})`);
              return;
            }
            if (!apiResponse.rel504 || apiResponse.rel504.length === 0) {
              toast.error(`Não há dados no período selecionado (${apiResponse.msg})`);
              return;
            }

            const report = apiResponse.rel504 as unknown as { [key: string]: string | number }[];

            ExportXlsx({
              filename: `RELATORIO DE ESTATISTICAS DE CONSULTAS GERADAS X RECLAMACOES GERADAS - Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: report,
            });
          },
          catchMessage: 'Não foi possível baixar o relatório',
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
        });
      },
      572: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: 0,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 572,
          },
          actionAfterResponse: (apiResponse: IReportApiResponse) => {
            if (!apiResponse.codigo) {
              toast.error(`Não foi possível baixar a planilha (${apiResponse.msg})`);
              return;
            }
            if (!apiResponse.rel572 || apiResponse.rel572.length === 0) {
              toast.error(`Não há dados no período selecionado (${apiResponse.msg})`);
              return;
            }

            const report = apiResponse.rel572 as unknown as { [key: string]: string | number }[];

            ExportXlsx({
              filename: 'Relatório Total de reclamações por período',
              data: report,
            });
          },
          catchMessage: 'Não foi possível baixar o relatório',
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
        });
      },
      50: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 50,
          },
          actionAfterResponse: (apiResponse: IReportApiResponse) => {
            if (!apiResponse.codigo) {
              toast.error(`Não foi possível baixar a planilha de otimização (${apiResponse.msg})`);
              return;
            }

            if (!apiResponse.temRegistros) {
              toast.error(`Não há dados no período selecionado (${apiResponse.msg})`);
              return;
            }

            const report = apiResponse.rel50 as unknown as { [key: string]: string | number }[];

            const mappedReportList = report?.map((rp) => ({
              CodFunc: rp.codFunc ?? '',
              CodConsulta: rp.codConsulta ?? '',
              CNPJ: rp.cnpj ?? '',
              Empresa: rp.empresa ?? '',
              CodLocal: rp.codLTAntes ?? '',
              DescLocal: rp.localTrabAntes ?? '',
              CodLocalDepois: rp.codLTAtual ?? '',
              DescLocalDepois: rp.localTrabAtual ?? '',
              CPF: rp.cpf ?? '',
              Matricula: rp.matricula ?? '',
              Nome: rp.nome ?? '',
              Logradouro: rp.funcLogradouro ?? '',
              Numero: rp.funcEndNum ?? '',
              Complem: rp.funcComplemento ?? '',
              'CEP Antes': rp.funcCEPAntes ?? '',
              'CEP Depois': rp.funcCEPAtual ?? '',
              'CEP DIF': onlyNumbers(String(rp.funcCEPAntes)) - onlyNumbers(String(rp.funcCEPAtual)),
              Bairro: rp.funcBairro ?? '',
              Cidade: rp.funcCidade ?? '',
              UF: rp.funcUF ?? '',
              'E-mail': rp.mail ?? '',
              HoraEntra: rp.horaEntra ?? '',
              HoraSai: rp.horaSai ?? '',
              TurnoDias: rp.TurnoDias ?? '',
              'Valor Dia': rp.valorVTRot ?? '',
              'Valor Mês': rp.valorVTRotMes ?? '',
              'StatusResultado': rp.statusResultado ?? '',
              'StatusConsulta': rp.statusConsulta ?? '',
              'CanceladoVT':  rp.canceladoVT ? 'SIM' : 'NÃO',
              'Mudança Cadastral': rp.mudouCadastro,
              'DataCadastro': String(rp.dataCadastro).includes('1900') ? '' : format(new Date(rp.dataCadastro), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              DataCadAtualizado: String(rp.dataCadAtualizado).includes('1900') ? '' : format(new Date(rp.dataCadAtualizado), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              'Data Inicial IA': String(rp.Data_Inicial_IA).includes('1900') ? '' : rp.Data_Inicial_IA,
              'Ultimo Envio IA': String(rp.Ultimo_Envio_IA).includes('1900') ? '' : rp.Ultimo_Envio_IA,
              Canal: rp.canal ?? '',
              InicioApp: String(rp.inicioApp).includes('1900') ? '' : format(new Date(rp.inicioApp), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              SituacaoCadastro: rp.situacaoCadastro ?? '',
              DataImplantacao: String(rp.dataImplantacao).includes('1900') ? '' : format(new Date(rp.dataImplantacao), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              GeradoPorBiasTag: rp.geradoPorBiasTag || 'Não especificado',
              CompraVT: rp.compraVT ?? '',
              CodVT1: rp.r1VT1_CodVT ?? '',
              DescVT1: rp.r1VT1_Descr ?? '',
              QtdaVT1: rp.r1VT1_Qtd ?? '',
              VlrVT1: rp.r1VT1_Tarifa ?? '',
              CodVT2: rp.r1VT2_CodVT ?? '',
              DescVT2: rp.r1VT2_Descr ?? '',
              QtdaVT2: rp.r1VT2_Qtd ?? '',
              VlrVT2: rp.r1VT2_Tarifa ?? '',
              CodVT3: rp.r1VT3_CodVT ?? '',
              DescVT3: rp.r1VT3_Descr ?? '',
              QtdaVT3: rp.r1VT3_Qtd ?? '',
              VlrVT3: rp.r1VT3_Tarifa ?? '',
              CodVT4: rp.r1VT4_CodVT ?? '',
              DescVT4: rp.r1VT4_Descr ?? '',
              QtdaVT4: rp.r1VT4_Qtd ?? '',
              VlrVT4: rp.r1VT4_Tarifa ?? '',
              CodVT5: rp.r1VT5_CodVT ?? '',
              DescVT5: rp.r1VT5_Descr ?? '',
              QtdaVT5: rp.r1VT5_Qtd ?? '',
              VlrVT5: rp.r1VT5_Tarifa ?? '',
              'Cartao URBS-PR': rp.Cartao_URBS_PR ?? '',
              'Dist a Pé': rp.distaciaPe ?? '',
              'DistOnibus_Ida': rp.distOnibus_Ida ?? '',
              'DistOnibus_Volta': rp.distOnibus_Volta ?? '',
              'DistTrem_Ida': rp.distTrem_Ida ?? '',
              'DistTrem_Volta': rp.distTrem_Volta ?? '',
            }));

            ExportXlsx({
              filename: `Relatório Otimização Pre Adm - Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: mappedReportList,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório',
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
        });
      },
      509: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 1,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 50,
          },
          actionAfterResponse: (apiResponse: IReportApiResponse) => {
            if (!apiResponse.codigo) {
              toast.error(`Não foi possível baixar a planilha de otimização (${apiResponse.msg})`);
              return;
            }

            if (!apiResponse.temRegistros) {
              toast.error(`Não há dados no período selecionado (${apiResponse.msg})`);
              return;
            }

            const report = apiResponse.rel50 as unknown as { [key: string]: string | number }[];

            const mappedReportList = report?.map((rp) => ({
              CodFunc: rp.codFunc ?? '',
              CodConsulta: rp.codConsulta ?? '',
              CNPJ: rp.cnpj ?? '',
              Empresa: rp.empresa ?? '',
              CodLocal: rp.codLTAntes ?? '',
              DescLocal: rp.localTrabAntes ?? '',
              CodLocalDepois: rp.codLTAtual ?? '',
              DescLocalDepois: rp.localTrabAtual ?? '',
              CPF: rp.cpf ?? '',
              Matricula: rp.matricula ?? '',
              Nome: rp.nome ?? '',
              Logradouro: rp.funcLogradouro ?? '',
              Numero: rp.funcEndNum ?? '',
              Complem: rp.funcComplemento ?? '',
              'CEP Antes': rp.funcCEPAntes ?? '',
              'CEP Depois': rp.funcCEPAtual ?? '',
              'CEP DIF': onlyNumbers(String(rp.funcCEPAntes)) - onlyNumbers(String(rp.funcCEPAtual)),
              Bairro: rp.funcBairro ?? '',
              Cidade: rp.funcCidade ?? '',
              UF: rp.funcUF ?? '',
              'E-mail': rp.mail ?? '',
              HoraEntra: rp.horaEntra ?? '',
              HoraSai: rp.horaSai ?? '',
              TurnoDias: rp.TurnoDias ?? '',
              'Valor Dia': rp.valorVTRot ?? '',
              'Valor Mês': rp.valorVTRotMes ?? '',
              'StatusResultado': rp.statusResultado ?? '',
              'StatusConsulta': rp.statusConsulta ?? '',
              'CanceladoVT':  rp.canceladoVT ? 'SIM' : 'NÃO',
              'Mudança Cadastral': rp.mudouCadastro,
              'DataCadastro': String(rp.dataCadastro).includes('1900') ? '' : format(new Date(rp.dataCadastro), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              DataCadAtualizado: String(rp.dataCadAtualizado).includes('1900') ? '' : format(new Date(rp.dataCadAtualizado), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              'Data Inicial IA': String(rp.Data_Inicial_IA).includes('1900') ? '' : rp.Data_Inicial_IA,
              'Ultimo Envio IA': String(rp.Ultimo_Envio_IA).includes('1900') ? '' : rp.Ultimo_Envio_IA,
              Canal: rp.canal ?? '',
              InicioApp: String(rp.inicioApp).includes('1900') ? '' : format(new Date(rp.inicioApp), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              SituacaoCadastro: rp.situacaoCadastro ?? '',
              DataImplantacao: String(rp.dataImplantacao).includes('1900') ? '' : format(new Date(rp.dataImplantacao), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              CompraVT: rp.compraVT ?? '',
              CodVT1: rp.r1VT1_CodVT ?? '',
              DescVT1: rp.r1VT1_Descr ?? '',
              QtdaVT1: rp.r1VT1_Qtd ?? '',
              VlrVT1: rp.r1VT1_Tarifa ?? '',
              CodVT2: rp.r1VT2_CodVT ?? '',
              DescVT2: rp.r1VT2_Descr ?? '',
              QtdaVT2: rp.r1VT2_Qtd ?? '',
              VlrVT2: rp.r1VT2_Tarifa ?? '',
              CodVT3: rp.r1VT3_CodVT ?? '',
              DescVT3: rp.r1VT3_Descr ?? '',
              QtdaVT3: rp.r1VT3_Qtd ?? '',
              VlrVT3: rp.r1VT3_Tarifa ?? '',
              CodVT4: rp.r1VT4_CodVT ?? '',
              DescVT4: rp.r1VT4_Descr ?? '',
              QtdaVT4: rp.r1VT4_Qtd ?? '',
              VlrVT4: rp.r1VT4_Tarifa ?? '',
              CodVT5: rp.r1VT5_CodVT ?? '',
              DescVT5: rp.r1VT5_Descr ?? '',
              QtdaVT5: rp.r1VT5_Qtd ?? '',
              VlrVT5: rp.r1VT5_Tarifa ?? '',
              'Cartao URBS-PR': rp.Cartao_URBS_PR ?? '',
              'Dist a Pé': rp.distaciaPe ?? '',
              'DistOnibus_Ida': rp.distOnibus_Ida ?? '',
              'DistOnibus_Volta': rp.distOnibus_Volta ?? '',
              'DistTrem_Ida': rp.distTrem_Ida ?? '',
              'DistTrem_Volta': rp.distTrem_Volta ?? '',
            }));

            ExportXlsx({
              filename: `Relatório Otimização Pre Adm - BILHETES SEM INTEGRACAO - Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: mappedReportList,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório',
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
        });
      },
      5011: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 50,
          },
          actionAfterResponse: (apiResponse: IReportApiResponse) => {
            if (!apiResponse.codigo) {
              toast.error(`Não foi possível baixar a planilha de otimização (${apiResponse.msg})`);
              return;
            }

            if (!apiResponse.temRegistros) {
              toast.error(`Não há dados no período selecionado (${apiResponse.msg})`);
              return;
            }

            const mappedData = apiResponse.rel50?.map((rel) => ({
              COD_CONSULTA: rel.codConsulta,
              DATA_ROTEIRIZACAO: rel.dataCadastro ? format(new Date(rel.dataCadastro), 'dd/MM/yyyy HH:mm') : '',
              CPF: rel.cpf,
              MATRICULA: rel.matricula,
              NOME: rel.nome,
              COMPRA_VT: rel.statusConsulta?.toUpperCase() ?? '',
              DATA_IMPLANTACAO: rel.dataImplantacao ? format(new Date(rel.dataImplantacao), 'dd/MM/yyyy HH:mm') : '',
              CANAL: rel.canal,
              COD_EMPRESA: selectedCompany.value,
              VTS: [
                { VT_COD: rel.r1VT1_CodVT, VT_DESCR: rel.r1VT1_Descr, VT_TARIFA: rel.r1VT1_Tarifa, VT_QTD: rel.r1VT1_Qtd || 1 },
                { VT_COD: rel.r1VT2_CodVT, VT_DESCR: rel.r1VT2_Descr, VT_TARIFA: rel.r1VT2_Tarifa, VT_QTD: rel.r1VT2_Qtd || 1 },
                { VT_COD: rel.r1VT3_CodVT, VT_DESCR: rel.r1VT3_Descr, VT_TARIFA: rel.r1VT3_Tarifa, VT_QTD: rel.r1VT3_Qtd || 1 },
                { VT_COD: rel.r1VT4_CodVT, VT_DESCR: rel.r1VT4_Descr, VT_TARIFA: rel.r1VT4_Tarifa, VT_QTD: rel.r1VT4_Qtd || 1 },
                { VT_COD: rel.r1VT5_CodVT, VT_DESCR: rel.r1VT5_Descr, VT_TARIFA: rel.r1VT5_Tarifa, VT_QTD: rel.r1VT5_Qtd || 1 }
              ]
            })) || [];

            const arrayWithEachVtPerRow = mappedData.reduce<VT[]>((acc, data) => {
              const validVts = data.VTS.filter(vt => vt.VT_COD); // Filtra apenas os objetos com VT_COD truthy
              validVts.forEach(vt => acc.push(
                {
                  COD_EMPRESA: String(data.COD_EMPRESA),
                  CPF: data.CPF,
                  NOME: data.NOME,
                  MATRICULA: String(data.MATRICULA),
                  COMPRA_VT: data.COMPRA_VT,
                  ...vt,
                })); // Adiciona ao array final
              return acc;
            }, []);

            ExportXlsx({
              filename: `Planilha de VT - Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: arrayWithEachVtPerRow as unknown as { [key: string]: string | number | Date; }[],
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório',
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
        });
      },
      5: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 5,
          },
          actionAfterResponse: (apiResponse: IReportApiResponse) => {
            if (!apiResponse.codigo) {
              toast.error(`Não foi possível baixar a planilha de otimização (${apiResponse.msg})`);
              return;
            }

            if (!apiResponse.temRegistros) {
              toast.error(`Não há dados no período selecionado (${apiResponse.msg})`);
              return;
            }

            const report = apiResponse.rel5 as unknown as { [key: string]: string | number }[];
            const mappedReportList = report?.map((rp) => ({
              CodFunc: rp.codFunc ?? '',
              CodConsulta: rp.codConsulta ?? '',
              CNPJ: rp.cnpj ?? '',
              Empresa: rp.empresa ?? '',
              CodLocal: rp.codLTArq ?? '',
              DescLocal: rp.localTrabArq ?? '',
              CodLocalAtual: rp.codLTAtual ?? '',
              DescLocalAtual: rp.localTrabAtual ?? '',
              CPF: rp.cpf ?? '',
              Matricula: rp.matricula ?? '',
              Nome: rp.nome ?? '',
              Logradouro: rp.funcLogradouro ?? '',
              Numero: rp.funcEndNum ?? '',
              Complem: rp.funcComplemento ?? '',
              CEPAntes: rp.funcCEPArq ?? '',
              CEPAtual: rp.funcCEPAtual ?? '',
              DifCEP: rp.diferencaCEP ?? '',
              Bairro: rp.funcBairro ?? '',
              Cidade: rp.funcCidade ?? '',
              UF: rp.funcUF ?? '',
              'E-mail': rp.mail ?? '',
              HoraEntra: rp.horaEntra ?? '',
              HoraSai: rp.horaSai ?? '',
              TurnoDias: rp.TurnoDias ?? '',
              'Valor Dia Atual': rp.valorVTInicial ?? '',
              'Valor Mês Atual': rp.valorVTInicialMes ?? '',
              'Valor dia Rot': rp.valorVTRot ?? '',
              'Valor Mês Rot': rp.valorVTRotMes ?? '',
              'Otimiz Dia': rp.otimizacaoDia ?? '',
              'Otimiz Mês': rp.otimizacaoMes ?? '',
              'Tem GS': rp.gestaoSaldo,
              'StatusResultado': rp.statusResultado ?? '',
              'StatusConsulta': rp.statusConsulta ?? '',
              'CanceladoVT': rp.canceladoVT ? 'SIM' : 'NÃO',
              'MudouCadastro': rp.mudouCadastro ? 'SIM' : 'NÃO',
              'DataCadastro': String(rp.dataCadastro).includes('1900') ? '' : format(new Date(rp.dataCadastro), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              DataCadAtualizado: String(rp.dataCadAtualizado).includes('1900') ? '' : format(new Date(rp.dataCadAtualizado), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              'Data Inicial IA': String(rp.Data_Inicial_IA).includes('1900') ? '' : rp.Data_Inicial_IA,
              'Ultimo Envio IA': String(rp.Ultimo_Envio_IA).includes('1900') ? '' : rp.Ultimo_Envio_IA,
              Canal: rp.canal ?? '',
              InicioApp: String(rp.inicioApp).includes('1900') ? '' : format(new Date(rp.inicioApp), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              SituacaoCadastro: rp.situacaoCadastro ?? '',
              DataImplantacao: String(rp.dataImplantacao).includes('1900') ? '' : format(new Date(rp.dataImplantacao), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              CompraVT: rp.compraVT ?? '',
              GeradoPorBiasTag: rp.geradoPorBiasTag || 'Não especificado',
              CodVT1: rp.r1VT1_CodVT ?? '',
              DescVT1: rp.r1VT1_Descr ?? '',
              QtdaVT1: rp.r1VT1_Qtd ?? '',
              VlrVT1: rp.r1VT1_Tarifa ?? '',
              CodVT2: rp.r1VT2_CodVT ?? '',
              DescVT2: rp.r1VT2_Descr ?? '',
              QtdaVT2: rp.r1VT2_Qtd ?? '',
              VlrVT2: rp.r1VT2_Tarifa ?? '',
              CodVT3: rp.r1VT3_CodVT ?? '',
              DescVT3: rp.r1VT3_Descr ?? '',
              QtdaVT3: rp.r1VT3_Qtd ?? '',
              VlrVT3: rp.r1VT3_Tarifa ?? '',
              CodVT4: rp.r1VT4_CodVT ?? '',
              DescVT4: rp.r1VT4_Descr ?? '',
              QtdaVT4: rp.r1VT4_Qtd ?? '',
              VlrVT4: rp.r1VT4_Tarifa ?? '',
              CodVT5: rp.r1VT5_CodVT ?? '',
              DescVT5: rp.r1VT5_Descr ?? '',
              QtdaVT5: rp.r1VT5_Qtd ?? '',
              VlrVT5: rp.r1VT5_Tarifa ?? '',
              'Cartao URBS-PR': rp.Cartao_URBS_PR ?? '',
              'Dist a Pé': rp.distaciaPe ?? '',
              'DistOnibus_Ida': rp.distOnibus_Ida ?? '',
              'DistOnibus_Volta': rp.distOnibus_Volta ?? '',
              'DistTrem_Ida': rp.distTrem_Ida ?? '',
              'DistTrem_Volta': rp.distTrem_Volta ?? '',
            }));

            ExportXlsx({
              filename: `Relatório Otimização Base - Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: mappedReportList,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório',
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
        });
      },
      59: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 1,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 5,
          },
          actionAfterResponse: (apiResponse: IReportApiResponse) => {
            if (!apiResponse.codigo) {
              toast.error(`Não foi possível baixar a planilha de otimização (${apiResponse.msg})`);
              return;
            }

            if (!apiResponse.temRegistros) {
              toast.error(`Não há dados no período selecionado (${apiResponse.msg})`);
              return;
            }

            const report = apiResponse.rel5 as unknown as { [key: string]: string | number }[];
            const mappedReportList = report?.map((rp) => ({
              CodFunc: rp.codFunc ?? '',
              CodConsulta: rp.codConsulta ?? '',
              CNPJ: rp.cnpj ?? '',
              Empresa: rp.empresa ?? '',
              CodLocal: rp.codLTArq ?? '',
              DescLocal: rp.localTrabArq ?? '',
              CodLocalAtual: rp.codLTAtual ?? '',
              DescLocalAtual: rp.localTrabAtual ?? '',
              CPF: rp.cpf ?? '',
              Matricula: rp.matricula ?? '',
              Nome: rp.nome ?? '',
              Logradouro: rp.funcLogradouro ?? '',
              Numero: rp.funcEndNum ?? '',
              Complem: rp.funcComplemento ?? '',
              CEPAntes: rp.funcCEPArq ?? '',
              CEPAtual: rp.funcCEPAtual ?? '',
              DifCEP: rp.diferencaCEP ?? '',
              Bairro: rp.funcBairro ?? '',
              Cidade: rp.funcCidade ?? '',
              UF: rp.funcUF ?? '',
              'E-mail': rp.mail ?? '',
              HoraEntra: rp.horaEntra ?? '',
              HoraSai: rp.horaSai ?? '',
              TurnoDias: rp.TurnoDias ?? '',
              'Valor Dia Atual': rp.valorVTInicial ?? '',
              'Valor Mês Atual': rp.valorVTInicialMes ?? '',
              'Valor dia Rot': rp.valorVTRot ?? '',
              'Valor Mês Rot': rp.valorVTRotMes ?? '',
              'Otimiz Dia': rp.otimizacaoDia ?? '',
              'Otimiz Mês': rp.otimizacaoMes ?? '',
              'Tem GS': rp.gestaoSaldo,
              'StatusResultado': rp.statusResultado ?? '',
              'StatusConsulta': rp.statusConsulta ?? '',
              'CanceladoVT': rp.canceladoVT ? 'SIM' : 'NÃO',
              'MudouCadastro': rp.mudouCadastro ? 'SIM' : 'NÃO',
              'DataCadastro': String(rp.dataCadastro).includes('1900') ? '' : format(new Date(rp.dataCadastro), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              DataCadAtualizado: String(rp.dataCadAtualizado).includes('1900') ? '' : format(new Date(rp.dataCadAtualizado), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              'Data Inicial IA': String(rp.Data_Inicial_IA).includes('1900') ? '' : rp.Data_Inicial_IA,
              'Ultimo Envio IA': String(rp.Ultimo_Envio_IA).includes('1900') ? '' : rp.Ultimo_Envio_IA,
              Canal: rp.canal ?? '',
              InicioApp: String(rp.inicioApp).includes('1900') ? '' : format(new Date(rp.inicioApp), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              SituacaoCadastro: rp.situacaoCadastro ?? '',
              DataImplantacao: String(rp.dataImplantacao).includes('1900') ? '' : format(new Date(rp.dataImplantacao), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
              CompraVT: rp.compraVT ?? '',
              CodVT1: rp.r1VT1_CodVT ?? '',
              DescVT1: rp.r1VT1_Descr ?? '',
              QtdaVT1: rp.r1VT1_Qtd ?? '',
              VlrVT1: rp.r1VT1_Tarifa ?? '',
              CodVT2: rp.r1VT2_CodVT ?? '',
              DescVT2: rp.r1VT2_Descr ?? '',
              QtdaVT2: rp.r1VT2_Qtd ?? '',
              VlrVT2: rp.r1VT2_Tarifa ?? '',
              CodVT3: rp.r1VT3_CodVT ?? '',
              DescVT3: rp.r1VT3_Descr ?? '',
              QtdaVT3: rp.r1VT3_Qtd ?? '',
              VlrVT3: rp.r1VT3_Tarifa ?? '',
              CodVT4: rp.r1VT4_CodVT ?? '',
              DescVT4: rp.r1VT4_Descr ?? '',
              QtdaVT4: rp.r1VT4_Qtd ?? '',
              VlrVT4: rp.r1VT4_Tarifa ?? '',
              CodVT5: rp.r1VT5_CodVT ?? '',
              DescVT5: rp.r1VT5_Descr ?? '',
              QtdaVT5: rp.r1VT5_Qtd ?? '',
              VlrVT5: rp.r1VT5_Tarifa ?? '',
              'Cartao URBS-PR': rp.Cartao_URBS_PR ?? '',
              'Dist a Pé': rp.distaciaPe ?? '',
              'DistOnibus_Ida': rp.distOnibus_Ida ?? '',
              'DistOnibus_Volta': rp.distOnibus_Volta ?? '',
              'DistTrem_Ida': rp.distTrem_Ida ?? '',
              'DistTrem_Volta': rp.distTrem_Volta ?? '',
            }));

            ExportXlsx({
              filename: `Relatório Otimização Base - BILHETES SEM INTEGRACAO - Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: mappedReportList,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório',
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
        });
      },
      5021: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 5,
          },
          actionAfterResponse: (apiResponse: IReportApiResponse) => {
            if (!apiResponse.codigo) {
              toast.error(`Não foi possível baixar a planilha de otimização (${apiResponse.msg})`);
              return;
            }

            if (!apiResponse.temRegistros) {
              toast.error(`Não há dados no período selecionado (${apiResponse.msg})`);
              return;
            }

            const mappedData = apiResponse.rel5.map((rel) => ({
              COD_CONSULTA: rel.codConsulta,
              DATA_ROTEIRIZACAO: rel.dataCadastro ? format(new Date(rel.dataCadastro), 'dd/MM/yyyy HH:mm') : '',
              CPF: rel.cpf,
              MATRICULA: rel.matricula,
              NOME: rel.nome,
              COMPRA_VT: rel.statusConsulta?.toUpperCase() ?? '',
              DATA_IMPLANTACAO: rel.dataImplantacao ? format(new Date(rel.dataImplantacao), 'dd/MM/yyyy HH:mm') : '',
              CANAL: rel.canal,
              VTS: [
                { VT_COD: rel.r1VT1_CodVT, VT_DESCR: rel.r1VT1_Descr, VT_TARIFA: rel.r1VT1_Tarifa, VT_QTD: rel.r1VT1_Qtd || 1 },
                { VT_COD: rel.r1VT2_CodVT, VT_DESCR: rel.r1VT2_Descr, VT_TARIFA: rel.r1VT2_Tarifa, VT_QTD: rel.r1VT2_Qtd || 1 },
                { VT_COD: rel.r1VT3_CodVT, VT_DESCR: rel.r1VT3_Descr, VT_TARIFA: rel.r1VT3_Tarifa, VT_QTD: rel.r1VT3_Qtd || 1 },
                { VT_COD: rel.r1VT4_CodVT, VT_DESCR: rel.r1VT4_Descr, VT_TARIFA: rel.r1VT4_Tarifa, VT_QTD: rel.r1VT4_Qtd || 1 },
                { VT_COD: rel.r1VT5_CodVT, VT_DESCR: rel.r1VT5_Descr, VT_TARIFA: rel.r1VT5_Tarifa, VT_QTD: rel.r1VT5_Qtd || 1 }
              ]
            }));

            const arrayWithEachVtPerRow = mappedData.reduce<VT[]>((acc, data) => {
              const validVts = data.VTS.filter(vt => vt.VT_COD); // Filtra apenas os objetos com VT_COD truthy
              validVts.forEach(vt => acc.push({
                COD_EMPRESA: String(selectedCompany.value),
                MATRICULA: String(data.MATRICULA),
                CPF: data.CPF,
                NOME: data.NOME,
                COMPRA_VT: data.COMPRA_VT,
                ...vt
              })); // Adiciona ao array final
              return acc;
            }, []);

            ExportXlsx({
              filename: `Planilha de VT - Emp_${selectedCompany.value}_${selectedCompany.label} ${specificPeriodLabel}`,
              data: arrayWithEachVtPerRow as unknown as { [key: string]: string | number | Date; }[],
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório',
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
        });
      },
      701: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: 0,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 701,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel700.map((item) => ({
              ...item,
              VENCIMENTO: item.VENCIMENTO.replaceAll('-', '/'),
            })) as unknown as { [key: string]: string | number }[];

            ExportXlsx({
              filename: `Controle Mensal Contas a Receber - Por Periodo Geracao Parcelas - ${specificPeriodLabel}`,
              data: report,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      702: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: 0,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 702,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel700.map((item) => ({
              ...item,
              VENCIMENTO: item.VENCIMENTO.replaceAll('-', '/'),
            })) as unknown as { [key: string]: string | number }[];

            ExportXlsx({
              filename: `Controle Mensal Contas a Receber - Por Periodo Vencimento Parcelas - ${specificPeriodLabel}`,
              data: report,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      703: async () => {
        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: selectedCompany.value,
            data1: getFirstAndLastPeriod(specificPeriod).first?.toISOString().split('T')[0],
            data2: getFirstAndLastPeriod(specificPeriod).last?.toISOString().split('T')[0],
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 703,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel700.map((item) => ({
              ...item,
              VENCIMENTO: item.VENCIMENTO.replaceAll('-', '/'),
            })) as unknown as { [key: string]: string | number }[];

            ExportXlsx({
              filename: `Controle Mensal Contas a Receber - Por Periodo Geracao Parcelas - Emp_${selectedCompany.value}_${selectedCompany.label} - ${specificPeriodLabel}`,
              data: report,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      704: async () => {
        if (!canAccessFinancial || userProfile >= 10) {
          toast.error('Você não tem permissão para acessar este relatório.');
          return;
        }

        const { month, year } = periodBeingSelected;
        const lastDayOfMonth = new Date(year, month, 0).getDate();
        const data1 = `${year}-${String(month).padStart(2, '0')}-01`;
        const data2 = `${year}-${String(month).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`;

        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: 0,
            data1,
            data2,
            valorInt: 0,
            valorInt1: 0,
            valorInt2: 0,
            valorLong: 0,
            tipoRel: 704,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros || !response.rel704 || response.rel704.length === 0) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const report = response.rel704 as unknown as { [key: string]: string | number }[];

            ExportXlsx({
              filename: `RELATÓRIO FINANCEIRO DE EMISSÃO DE NOTA FISCAL - OMIE - PERÍODO ${String(month).padStart(2, '0')}-${year} EM ${format(new Date(), 'dd-MM-yyyy')}`,
              data: report,
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      521: async () => {
        const { month, year } = periodBeingSelected;
        const lastDayOfMonth = new Date(year, month, 0).getDate();
        const data1 = `${year}-${String(month).padStart(2, '0')}-01`;
        const data2 = `${year}-${String(month).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`;

        await apiCall({
          apiToCall: ConsultsService.getGeneralReports,
          queryParams: {
            codUsu: userCode,
            perfilUsu: userProfile,
            codEmpresa: 0,
            data1,
            data2,
            valorInt: 1,
            valorInt1: month,
            valorInt2: year,
            valorLong: selectedPartner.value,
            tipoRel: 521,
          },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          actionAfterResponse: (response: IReportApiResponse) => {
            if (!response.codigo) {
              toast.error(`Não foi possível baixar o relatório (${response.msg})`);
              return;
            }

            if (!response.temRegistros) {
              toast.error(`Não há dados no período selecionado (${response.msg})`);
              return;
            }

            const sheetConsultas = (response.rel521 ?? []).map((item) => ({
              'CNPJ': item.CNPJ ?? '',
              'EMPRESA': item.EMPRESA ?? '',
              'MATRÍCULA': item.MATRICULA ?? '',
              'CPF': item.CPF ?? '',
              'NOME': item.NOME ?? '',
              'CONSULTA': item.CONSULTA ?? '',
              'CANAL': item.CANAL ?? '',
              'DATA GERAÇÃO': item.DATA_GERACAO ? format(new Date(item.DATA_GERACAO), 'dd/MM/yyyy') : '',
              'HORA GERAÇÃO': item.HORA_GERACAO ?? '',
              'PARCEIRO': item.PARCEIRO ?? '',
              'TRECHO': item.TRECHO ?? '',
            }));

            const sheetAdmissaoOnline = (response.rel521_0 ?? []).map((item) => ({
              'CÓD. EMPRESA': item.COD_EMPRESA ?? '',
              'EMPRESA': item.EMPRESA ?? '',
              'VALOR': item.VALOR ?? 0,
            }));

            const sheetRotBase = (response.rel521_1 ?? []).map((item) => ({
              'CÓD. EMPRESA': item.COD_EMPRESA ?? '',
              'EMPRESA': item.EMPRESA ?? '',
              'VALOR': item.VALOR ?? 0,
              'PARCELA': item.PARCELA ?? '',
            }));

            const sheetOutros = (response.rel521_2 ?? []).map((item) => ({
              'CÓD. EMPRESA': item.COD_EMPRESA ?? '',
              'EMPRESA': item.EMPRESA ?? '',
              'VALOR': item.VALOR ?? 0,
              'PARCELA': item.PARCELA ?? '',
            }));

            const sheetTotal = (response.rel521_3 ?? []).map((item) => ({
              'ITENS': item.ITENS ?? '',
              'VALORES': item.VALORES ?? 0,
            }));

            ExportXlsx({
              filename: `Relatório de Faturamento Mensal Detalhado - ${selectedPartner.label} - ${monthsMap[month]}/${year}`,
              sheets: [
                { name: 'CONSULTAS', data: sheetConsultas as unknown as { [key: string]: string | number | Date; }[] },
                { name: 'PLATAFORMA ADMISSÃO ONLINE', data: sheetAdmissaoOnline as unknown as { [key: string]: string | number | Date; }[] },
                { name: 'ROT BASE', data: sheetRotBase as unknown as { [key: string]: string | number | Date; }[] },
                { name: 'OUTROS', data: sheetOutros as unknown as { [key: string]: string | number | Date; }[] },
                { name: 'TOTAL', data: sheetTotal as unknown as { [key: string]: string | number | Date; }[] },
              ],
            });

            setDownloadModalShow(false);
          },
          catchMessage: 'Não foi possível baixar o relatório.',
        });
      },
      23: async () => {
        const findCorrespondingLabel = (value: number, list: { value: number | null, label: string }[]) => {
          const correspondingObject = list.find((obj) => obj.value === value);
          return correspondingObject?.label;
        };

        await apiCall({
          apiToCall: VtItensService.listVtItens,
          queryParams: { codUsu: userCode, perfilUsu: userProfile },
          onStartLoad: () => setIsLoading(true),
          onEndLoad: () => setIsLoading(false),
          catchMessage: 'Não foi posível baixar. Por favor, tente novamente',
          actionAfterResponse: (response: VtItensListApiResponse) => {
            const { codigo, msg, listaItensVT, listaOperadoras } = response;
            if (!codigo) {
              toast.error(`Não foi possível baixar. Por favor, tente novamente (${msg})`);
              return;
            }

            const vtItensMapped = listaItensVT.map((item) => ({
              code: item.codItem,
              geoCode: item.codItemRot,
              operatorCode: item.codOperadora,
              fare: floatToCurrency(item.tarifa),
              description: item.descricao,
              transport: item.codTipoTransp,
              status: (item.ativo ? 'Ativo' : 'Inativo') as 'Ativo' | 'Inativo',
              uf: item.ufOp,
              hasLines: item.temLinha,
              region: item.regiao,
            }));
            const operators = listaOperadoras.map((operator) => ({ value: operator.codOperadora, label: operator.nomeOp }));
            const filteredItens = vtItensMapped.filter((item) => item.status === 'Ativo');
            const mappedVtItens = filteredItens.map((vtItem) => ({
              UF: vtItem.uf,
              Regiao: vtItem.region,
              'Código operadora': vtItem.operatorCode,
              'Operadora': findCorrespondingLabel(vtItem.operatorCode, operators) ?? '',
              'Descrição': vtItem.description,
              'Tarifa': vtItem.fare,
              'Código item': vtItem.code,
              'Código GEO': vtItem.geoCode,
              'Transporte': findCorrespondingLabel(vtItem.transport, operators) ?? '',
              'Tem Linha': vtItem.hasLines,
            }));

            const currentDate = getCurrentDateWithHyphen();
            ExportXlsx({
              filename: `Itens VT (habilitados) Capta - ${currentDate}`,
              data: mappedVtItens,
            });
          },
        });
      }
    };

    await downloadReportLiterals[selectedReport.value]();
  }, [selectedReport.value, apiCall, userCode, userProfile, selectedCompany.value, selectedCompany.label, selectedFileType, selectedFile.value, selectedFile.label, setIsLoading, specificPeriodLabel, setDownloadModalShow, specificPeriod, isCustomer, usersCompanyCode, generatedConsultsReportType, periodBeingSelected, selectedPartner.value, selectedPartner.label, monthsMap, canAccessFinancial]);

  const canDownloadReport: { [key: number]: boolean } = useMemo(() => ({
    60: !!(specificPeriod && selectedReport.value && selectedCompany.value && selectedFile.value),
    601: !!(specificPeriod && selectedReport.value && selectedCompany.value),
    604: !!(selectedReport.value && selectedCompany.value),
    600: !!(specificPeriod && selectedReport.value && selectedCompany.value && selectedFile.value),
    6010: !!(specificPeriod && selectedReport.value && selectedCompany.value),
    503: !!(specificPeriod && selectedReport.value && selectedCompany.value),
    61: !!(specificPeriod && selectedReport.value && selectedCompany.value),
    62: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value && selectedFile.value),
    501: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value && selectedFile.value),
    5019: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value && selectedFile.value),
    5012: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value && selectedFile.value),
    5022: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value && selectedFile.value),
    502: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value && selectedFile.value),
    5029: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value && selectedFile.value),
    52: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && (((selectedCompany.value || isCustomer) && generatedConsultsReportType === 'byCompany') || (generatedConsultsReportType === 'all') || (generatedConsultsReportType === 'byPartner' && selectedPartner.value))),
    55: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && ((generatedConsultsReportType === 'all') || (generatedConsultsReportType === 'byPartner' && selectedPartner.value))),
    550: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && ((generatedConsultsReportType === 'all') || (generatedConsultsReportType === 'byPartner' && selectedPartner.value))),
    50: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value),
    509: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value),
    5011: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value),
    5021: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value),
    5: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value),
    59: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value),
    57: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value),
    504: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value),
    572: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value),
    571: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedCompany.value),
    23: true,
    701: !!(specificPeriod && selectedReport.value),
    702: !!(specificPeriod && selectedReport.value),
    703: !!(specificPeriod && selectedReport.value && selectedCompany.value),
    521: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && selectedPartner.value),
    704: !!(periodBeingSelected.month && periodBeingSelected.year && selectedReport.value && canAccessFinancial && userProfile < 10),
  }), [generatedConsultsReportType, isCustomer, periodBeingSelected.month, periodBeingSelected.year, selectedCompany.value, selectedFile.value, selectedPartner.value, selectedReport.value, specificPeriod, canAccessFinancial, userProfile]);

  const loadListsForSearching = useCallback(async (month?: number, year?: number) => {
    await apiCall({
      apiToCall: ConsultsService.loadListsForSearchingConsults,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: selectedCompany.value,
        mes: month || (new Date()).getMonth() + 1,
        ano: year || (new Date()).getFullYear(),
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response: ListsForSearchingConsultsApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível carregar os arquivos contidos no período (${response.msg})`);
          return;
        }

        const { listaArqsMes, listaArqaBaseMes } = response;

        const onlyMrFiles = listaArqsMes?.filter((x) => x.tipoArq === 2);
        const onlyPreAdmFiles = listaArqsMes?.filter((x) => x.tipoArq === 1);
        const onlyBaseFiles = listaArqaBaseMes;

        setMrFilesOptions(onlyMrFiles?.map((x) => ({
          value: String(x.codArq),
          label: `${x.denominacaoArq} | ${format(new Date(x.dataArq), 'dd/MM/yyyy')} | ${x.statusProc} (${x.totProcessados} de ${x.totRoteirizacoes})`,
          fileType: 'preAdm',
        })));

        setPreAdmFilesOptions(onlyPreAdmFiles?.map((x) => ({
          value: String(x.codArq),
          label: `${x.denominacaoArq} | ${format(new Date(x.dataArq), 'dd/MM/yyyy')} | ${x.statusProc} (${x.totProcessados} de ${x.totRoteirizacoes})`,
          fileType: 'preAdm',
        })));

        setBaseFilesOptions(onlyBaseFiles?.map((x) => ({
          value: String(x.codArq),
          label: `${x.denominacaoArq} | ${format(new Date(x.dataArq), 'dd/MM/yyyy')} | ${x.statusProc} (${x.totProcessados} de ${x.totRoteirizacoes})`,
          fileType: 'base',
        })));
      }
    }
    );
  }, [apiCall, selectedCompany.value, setIsLoading, userCode, userProfile]);

  const loadPartnersList = useCallback(async () => {
    await apiCall({
      apiToCall: ConsultsService.getPartnersList,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: selectedCompany.value,
        tipoSel: 2,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response: GetPartnersListApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível carregar os arquivos contidos no período (${response.msg})`);
          return;
        }

        const { ListaParceiros } = response;

        setPartnerOptions(ListaParceiros.map((x) => ({
          value: x.codParceiro,
          label: x.parceiro,
        })));
      }
    }
    );
  }, [apiCall, selectedCompany.value, setIsLoading, userCode, userProfile]);

  const getCompaniesList = useCallback(async () => {
    if (userProfile < 10) {
      await apiCall({
        apiToCall: CompanyService.getActiveCompaniesList,
        queryParams: { codUsu: userCode, perfilUsu: userProfile, codEmpresa: usersCompanyCode },
        onStartLoad: () => setIsLoading(true),
        onEndLoad: () => setIsLoading(false),
        actionAfterResponse: (response) => {
          if (!response.codigo) {
            toast.error(`Não foi possível recuperar a lista de empresas. Por favor, tente novamente (${response.msg})`);
            return;
          }
          const companies: CompanyFromApiType[] = response.listaEmpresas;
          const mappedCompaniesList = companies.map((x) => ({ value: x.codEmpresa, label: x.nomeFantasia }));
          setCompaniesList(mappedCompaniesList ?? []);
        }
      });
    } else {
      await apiCall({
        apiToCall: CompanyService.getCompanyData,
        onStartLoad: () => setIsLoading(true),
        onEndLoad: () => setIsLoading(false),
        queryParams: {
          codEmpresa: usersCompanyCode,
          codUsu: userCode,
          perfilUsu: userProfile,
        },
        actionAfterResponse: (response: GetCompanyDataResponseType) => {
          if (!response.codigo) {
            toast.error(`Não foi possível carregar a lista de empresas. Por favor, tente novamente (${response.msg})`);
            return;
          }
          if (!response.listaEmpresas) {
            setCompaniesList([{ value: usersCompanyCode, label: usersCompanyLabel }]);
            return;
          }
          const companies = response.listaEmpresas;
          const mappedCompaniesList = companies.map((x) => ({ value: x.codEmpresa, label: x.nomeFantasia }));
          setCompaniesList(mappedCompaniesList ?? []);
        },
        catchMessage: 'Não foi possível carregar a lista de filiais. Por favor, tente novamente.',
      });
    }

  }, [apiCall, setIsLoading, userCode, userProfile, usersCompanyCode, usersCompanyLabel]);

  useEffect(() => {
    const reportsWithMonthYearPeriod = [62, 501, 5019, 502, 5029, 5012, 5022];
    if (reportsWithMonthYearPeriod.includes(selectedReport.value) && selectedCompany.value) {
      console.log('entrou', selectedReport.value);
      loadListsForSearching(periodBeingSelected.month, periodBeingSelected.year);
      setSelectedFile({} as OptionType);
    }
  }, [loadListsForSearching, periodBeingSelected, selectedCompany.value, selectedReport.value]);

  useEffect(() => {
    if ((selectedReport.value === 52 || selectedReport.value === 55 || selectedReport.value === 550) && generatedConsultsReportType === 'byPartner' && partnersOptions.length === 0) {
      loadPartnersList();
    }
    if (selectedReport.value === 521 && partnersOptions.length === 0) {
      loadPartnersList();
    }
  }, [generatedConsultsReportType, loadListsForSearching, loadPartnersList, partnersOptions.length, periodBeingSelected, selectedCompany.value, selectedReport.value]);

  useEffect(() => {
    if (downloadModalShow) {
      getCompaniesList();
    }
  }, [downloadModalShow, getCompaniesList]);

  const cleanAllStates = useCallback(() => {
    setSelectedFile({} as OptionType);
    setSelectedFileType('base');
    setSelectedPartner({} as OptionType);
    setGeneratedConsultsReportType('all');
  }, []);

  useEffect(() => {
    cleanAllStates();
  }, [cleanAllStates, selectedCompany]);

  useEffect(() => {
    cleanAllStates();
    setSelectedCompany({} as OptionType);
    setPeriodBeingSelected({ month: currentMonth, year: currentYear });
  }, [cleanAllStates, currentMonth, currentYear, selectedReport.value]);

  return {
    downloadReport,
    handleSpecificPeriodChange,
    specificPeriod,
    specificPeriodLabel,
    reportOptions: isDifferentSubdomain ? reportOptionsWhenDifferentSubdomain : reportOptions,
    selectedReport,
    setSelectedReport,
    companiesOptions,
    selectedCompany,
    setSelectedCompany,
    filesBySelectedFileType,
    selectedFileType,
    setSelectedFileType,
    setSelectedFile,
    selectedFile,
    handleSpecificPeriodChangeMrReport,
    canDownloadReport,
    setPeriodBeingSelected,
    currentYear,
    periodBeingSelected,
    months,
    currentMonth,
    monthsMap,
    fileOptions: mrFilesOptions,
    handleSelectedFileChange,
    partnersOptions,
    selectedPartner,
    setSelectedPartner,
    generatedConsultsReportType,
    setGeneratedConsultsReportType,
    baseFilesOptions,
    preAdmFilesOptions,
    isCustomer
  };
}
